/* eslint-disable @typescript-eslint/no-explicit-any */
import { signal, computed, Signal, WritableSignal } from '@angular/core';
import { Observable, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
} from '@progress/kendo-data-query';
import { FilterUtils } from '../../Data-Query/filter-utils';
import * as _ from 'lodash-es';
import { ODataExSettings } from '@ups/xplat/features';

/**
 * Signal-based GroupingExViewModel for modern Angular with better reactivity
 *
 * Usage:
 * ```typescript
 * this.viewModel = new GroupingExSignalViewModel(
 *   (odataQuery) => this.service.query(odataQuery),
 *   (left, right) => left.id === right.id
 * ).configure(config => {
 *   config.loadDataOnInit = false;
 * });
 * ```
 */
export class GroupingExSignalViewModel {
  // Configuration signals
  public readonly loadDataOnInit = signal(true);
  public readonly loadGroupsByLevels = signal(true);
  public readonly loadAllItemsToGroups = signal(false);
  public readonly autoExpandGroupOnSelection = signal(false);
  public readonly loadAllItemsUntilCountOf = signal(25);

  // State signals
  public readonly loading: WritableSignal<boolean> = signal(false);
  public readonly error: WritableSignal<Error | null> = signal(null);
  public readonly lastLoadedData: WritableSignal<any> = signal(null);

  // Configuration (non-signal, as they're typically set once)
  public linkedAggregateFields: string[][] = [];
  public customODataAggregateExpression: string = undefined;
  public extraFilter: FilterDescriptor | CompositeFilterDescriptor;
  public extraFilterLogic: 'and' | 'or' = 'and';
  public oDataSettings: ODataExSettings;
  public oDataOpenTypesFieldName: string;

  // Computed signals
  public readonly isLoading: Signal<boolean> = computed(() => this.loading());
  public readonly hasError: Signal<boolean> = computed(
    () => this.error() !== null
  );

  constructor(
    private dataLoadCallback: (
      odataQuery: string,
      signal?: AbortSignal
    ) => Observable<any> | Promise<any>,
    private equalityComparerCallback?: (left: any, right: any) => boolean
  ) {
    // Constructor
  }

  /**
   * Load data and update signals
   */
  public async loadData(
    odataQuery: string,
    signal?: AbortSignal
  ): Promise<any> {
    this.error.set(null);

    try {
      if (signal?.aborted) {
        throw new Error('Aborted');
      }

      const result = this.dataLoadCallback(odataQuery, signal);

      let data: any;
      if (result instanceof Observable) {
        if (signal) {
          const cancel$ = new Subject<void>();
          const abortHandler = () => {
            cancel$.next();
            cancel$.complete();
          };
          signal.addEventListener('abort', abortHandler, { once: true });
          try {
            data = await firstValueFrom(result.pipe(takeUntil(cancel$)));
          } finally {
            signal.removeEventListener('abort', abortHandler);
          }
        } else {
          data = await firstValueFrom(result);
        }
      } else {
        data = await result;
      }

      if (signal?.aborted) {
        throw new Error('Aborted');
      }

      this.lastLoadedData.set(data);
      return data;
    } catch (err) {
      if (err instanceof Error && err.message === 'Aborted') {
        return null;
      }
      // If firstValueFrom throws EmptyError because of takeUntil, treat as aborted
      if (err.name === 'EmptyError') {
        return null;
      }
      const error = err instanceof Error ? err : new Error(String(err));
      this.error.set(error);
      console.error('GroupingEx data load error:', error);
      throw error;
    }
  }

  /**
   * Get data as Observable (for backward compatibility)
   */
  public getDataLoadObservable(odataQuery: string): Observable<any> {
    const result = this.dataLoadCallback(odataQuery);
    return result instanceof Observable
      ? result
      : new Observable((subscriber) => {
          (result as Promise<any>)
            .then((data) => {
              subscriber.next(data);
              subscriber.complete();
            })
            .catch((err) => subscriber.error(err));
        });
  }

  public getEqualityComparer(): (left: any, right: any) => boolean {
    return this.equalityComparerCallback || ((left, right) => left === right);
  }

  public configure(
    configureCallback: (config: GroupingExSignalViewModel) => void
  ): GroupingExSignalViewModel {
    configureCallback(this);
    return this;
  }

  public createStateWithExtraFilter(state: State): State {
    const stateCopy = _.cloneDeep(state) as State;
    FilterUtils.replaceFilter(
      stateCopy,
      FilterUtils.concatFilters(
        stateCopy.filter,
        this.extraFilter,
        this.extraFilterLogic
      )
    );
    return stateCopy;
  }

  /**
   * Clear error state
   */
  public clearError(): void {
    this.error.set(null);
  }

  /**
   * Reset all state
   */
  public reset(): void {
    this.loading.set(false);
    this.error.set(null);
    this.lastLoadedData.set(null);
  }
}
